import { countChars, countOccurrences, extractHeadings, paragraphs, splitSentences, stripMarkdown } from "./markdown.ts";

// Article length bounds from the brief (§12, §15).
export const MIN_CHARS = 3000;
export const MAX_CHARS = 7000;

export interface SeoInput {
  title: string;
  metaDescription: string;
  body: string;
  keyword: string;
  relatedKeywords: string[];
  faqCount: number;
}

export type CheckStatus = "pass" | "warn" | "fail";

export interface SeoCheck {
  id: string;
  label: string;
  status: CheckStatus;
  message: string;
  weight: number;
}

interface Ctx extends SeoInput {
  plain: string;
  chars: number;
  headings: ReturnType<typeof extractHeadings>;
  sentences: string[];
  paragraphs: string[];
}

type Rule = (c: Ctx) => SeoCheck;

// One function per check; adding a check is one entry.
const RULES: Rule[] = [
  (c) => {
    const n = [...c.title].length;
    const b = { id: "title-length", label: "タイトルの長さ", weight: 10 };
    if (n === 0) return { ...b, status: "fail", message: "タイトルがありません" };
    if (n < 25 || n > 40) return { ...b, status: "warn", message: `${n}字（目安は28〜40字）` };
    return { ...b, status: "pass", message: `${n}字` };
  },
  (c) => {
    const b = { id: "title-keyword", label: "タイトルにキーワード", weight: 12 };
    const k = c.keyword.trim().toLowerCase();
    const i = c.title.toLowerCase().indexOf(k);
    if (!k) return { ...b, status: "warn", message: "キーワードが未設定です" };
    if (i === -1) return { ...b, status: "fail", message: `「${c.keyword}」が含まれていません` };
    return { ...b, status: i <= 15 ? "pass" : "warn", message: i <= 15 ? "前半に含まれています" : "前半に置くと効果的です" };
  },
  (c) => {
    const n = [...c.metaDescription].length;
    const b = { id: "meta", label: "メタディスクリプション", weight: 8 };
    if (n === 0) return { ...b, status: "fail", message: "ありません" };
    if (n < 80 || n > 130) return { ...b, status: "warn", message: `${n}字（目安は80〜120字）` };
    return { ...b, status: "pass", message: `${n}字` };
  },
  (c) => {
    const b = { id: "length", label: "本文の文字数", weight: 14 };
    if (c.chars < MIN_CHARS) return { ...b, status: c.chars < MIN_CHARS * 0.7 ? "fail" : "warn", message: `${c.chars.toLocaleString()}字（${MIN_CHARS.toLocaleString()}字以上が必要）` };
    if (c.chars > MAX_CHARS) return { ...b, status: "warn", message: `${c.chars.toLocaleString()}字（上限 ${MAX_CHARS.toLocaleString()}字）` };
    return { ...b, status: "pass", message: `${c.chars.toLocaleString()}字` };
  },
  (c) => {
    const b = { id: "intro", label: "冒頭で答えているか", weight: 8 };
    if (!c.keyword.trim()) return { ...b, status: "warn", message: "キーワードが未設定です" };
    const firstHeading = c.body.search(/^##\s/m);
    const intro = stripMarkdown(firstHeading > 0 ? c.body.slice(0, firstHeading) : c.body.slice(0, 400));
    if (!intro.trim()) return { ...b, status: "fail", message: "最初の見出しの前に導入文がありません" };
    return countOccurrences(intro, c.keyword) > 0
      ? { ...b, status: "pass", message: "導入文でキーワードに触れています" }
      : { ...b, status: "warn", message: "導入文でキーワードに触れましょう" };
  },
  (c) => {
    const b = { id: "density", label: "キーワードの出現", weight: 8 };
    if (!c.keyword.trim() || c.chars === 0) return { ...b, status: "warn", message: "本文またはキーワードがありません" };
    const n = countOccurrences(c.plain, c.keyword);
    const d = (n * [...c.keyword].length) / c.chars;
    if (n === 0) return { ...b, status: "fail", message: "本文に出てきません" };
    if (d > 0.035) return { ...b, status: "warn", message: `${n}回。多すぎる可能性があります` };
    return { ...b, status: "pass", message: `${n}回` };
  },
  (c) => {
    // 共起語 help coverage, but not every one belongs in every article: 6 in 10 is enough.
    const b = { id: "related", label: "共起語", weight: 6 };
    const terms = c.relatedKeywords.filter(Boolean);
    if (terms.length === 0) return { ...b, status: "pass", message: "設定なし" };
    const missing = terms.filter((k) => countOccurrences(c.plain, k) === 0);
    const used = terms.length - missing.length;
    if (used / terms.length >= 0.6) return { ...b, status: "pass", message: `${terms.length}語中 ${used}語を使用` };
    return { ...b, status: "warn", message: `${terms.length}語中 ${used}語を使用。未使用：${missing.slice(0, 5).join("、")}` };
  },
  (c) => {
    const b = { id: "h2", label: "見出しの数", weight: 8 };
    const h2 = c.headings.filter((h) => h.level === 2).length;
    if (h2 === 0) return { ...b, status: "fail", message: "大見出し（H2）がありません" };
    return { ...b, status: h2 >= 3 ? "pass" : "warn", message: `H2 が ${h2} 個` };
  },
  (c) => {
    const b = { id: "faq", label: "FAQ", weight: 4 };
    return c.faqCount > 0 ? { ...b, status: "pass", message: `${c.faqCount}件` } : { ...b, status: "warn", message: "FAQ があると検索結果で目立ちます" };
  },
  (c) => {
    const b = { id: "sentence", label: "一文の長さ", weight: 6 };
    if (c.sentences.length === 0) return { ...b, status: "fail", message: "本文がありません" };
    const long = c.sentences.filter((s) => [...s].length > 80).length;
    return long / c.sentences.length > 0.15 ? { ...b, status: "warn", message: `80字を超える文が ${long} 文` } : { ...b, status: "pass", message: "読みやすい長さです" };
  },
  (c) => {
    const b = { id: "paragraph", label: "段落の長さ", weight: 4 };
    const long = c.paragraphs.filter((p) => [...stripMarkdown(p)].length > 300).length;
    return long > 0 ? { ...b, status: "warn", message: `300字を超える段落が ${long} 個` } : { ...b, status: "pass", message: "適切です" };
  },
];

const CREDIT: Record<CheckStatus, number> = { pass: 1, warn: 0.5, fail: 0 };

export function analyzeSeo(input: SeoInput): { score: number; checks: SeoCheck[] } {
  const plain = stripMarkdown(input.body);
  const ctx: Ctx = { ...input, plain, chars: countChars(input.body), headings: extractHeadings(input.body), sentences: splitSentences(plain), paragraphs: paragraphs(input.body) };
  const checks = RULES.map((r) => r(ctx));
  const total = checks.reduce((a, c) => a + c.weight, 0);
  const earned = checks.reduce((a, c) => a + c.weight * CREDIT[c.status], 0);
  return { score: Math.round((earned / total) * 100), checks };
}
